import mqtt, { MqttClient, IClientOptions, Packet } from "mqtt";
// MQTT 客户端实例（全局唯一）
let mqttClient: MqttClient | null = null;
// 连接状态（全局可判断：true=已连接，false=未连接/断开）
let isConnected = false;
// 全局错误信息（连接/操作失败时更新）
let globalError: Error | null = null;
/**
 * 建立 MQTT 连接（含事件监听、重连逻辑）
 */
const initMqtt = (option: { options: IClientOptions; callback?: any }) => {
  try {
    // MQTT 客户端配置
    const options: IClientOptions = option.options;


    // 拼接 WebSocket 连接地址（浏览器必用格式）
    const connectUrl = `wss://${options.host}/wss`;
    // 创建并连接客户端
    console.log("=-=-=-=-=", connectUrl);
    mqttClient = mqtt.connect(connectUrl, options);
    console.log(`📌 全局 MQTT 正在初始化，连接地址：${options.host}，客户端ID：${options.clientId}`);

    // 监听连接成功事件
    mqttClient.on("connect", () => {
      isConnected = true;
      globalError = null;
      console.log("✅ 全局 MQTT 连接成功（SolidJS 项目启动时初始化）");
      option.callback?.();
    });

    // 监听连接错误事件
    mqttClient.on("error", (err) => {
      isConnected = false;
      globalError = err as Error;
      console.error("❌ 全局 MQTT 连接错误：", err);
    });

    // 监听断开连接事件
    mqttClient.on("close", () => {
      isConnected = false;
      console.warn("⚠️ 全局 MQTT 连接已断开，将按配置自动重连");
    });

    // 监听重连中事件
    mqttClient.on("reconnect", () => {
      console.log("🔄 全局 MQTT 正在重连...");
    });
  } catch (err) {
    isConnected = false;
    globalError = err as Error;
    console.error("❌ 全局 MQTT 初始化失败：", err);
  }
};

// 🌟 模块加载时立即执行初始化 → SolidJS 项目启动即连接 EMQX

// ===================== 4. 全局通用方法封装（全项目可导入使用）=====================
/**
 * 发布 MQTT 消息
 * @param topic 消息主题（如 solidjs/global/msg）
 * @param message 消息内容（字符串/对象，对象自动序列化为JSON）
 * @param qos QoS 等级（0/1/2，默认 0）
 * @returns Promise<boolean> 发布成功/失败
 */
const publish = async (topic: string, message: string | object, qos: 0 | 1 | 2 = 0): Promise<boolean> => {
  if (!mqttClient || !isConnected) {
    const err = new Error("全局 MQTT 未连接，无法发布消息");
    globalError = err;
    throw err;
  }

  // 对象消息自动序列化
  const payload = typeof message === "object" ? JSON.stringify(message) : message;

  return new Promise((resolve, reject) => {
    mqttClient!.publish(topic, payload, { qos }, (err) => {
      if (err) {
        globalError = err as Error;
        console.error(`❌ MQTT发布失败 [${topic}]：`, err);
        reject(err);
      } else {
        console.log(`📤 MQTT发布成功 [${topic}]：`, payload);
        resolve(true);
      }
    });
  });
};

/**
 * 订阅 MQTT 主题
 * @param topic 订阅主题（支持单个/数组，如 'solidjs/test' 或 ['a/b', 'c/d']）
 * @param qos QoS 等级（0/1/2，默认 0）
 * @returns Promise<Packet[]> 订阅成功的主题列表
 */
const subscribe = async (topic: string | string[], qos: 0 | 1 | 2 = 0): Promise<Packet[]> => {
  if (!mqttClient || !isConnected) {
    const err = new Error("全局 MQTT 未连接，无法订阅主题");
    globalError = err;
    throw err;
  }

  return new Promise((resolve, reject) => {
    mqttClient!.subscribe(topic, { qos }, (err, granted: any) => {
      // console.log(`📌 MQTT订阅中...!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!`, err);

      if (err) {
        globalError = err as Error;
        console.error(`❌ MQTT订阅失败 [${topic}]：`, err);
        reject(err);
      } else {
        const topics = granted.map((item: any) => item.topic).join(", ");
        console.log(`📌 MQTT订阅成功：${topics}`);
        resolve(granted);
      }
    });
  });
};

/**
 * 取消订阅 MQTT 主题
 * @param topic 取消的主题（支持单个/数组）
 * @returns Promise<boolean> 取消成功/失败
 */
const unsubscribe = async (topic: string | string[]): Promise<boolean> => {
  if (!mqttClient || !isConnected) {
    const err = new Error("全局 MQTT 未连接，无法取消订阅");
    globalError = err;
    throw err;
  }

  return new Promise((resolve, reject) => {
    mqttClient!.unsubscribe(topic, (err) => {
      if (err) {
        globalError = err as Error;
        console.error(`❌ MQTT取消订阅失败 [${topic}]：`, err);
        reject(err);
      } else {
        console.log(`✂️ MQTT取消订阅成功 [${topic}]`);
        resolve(true);
      }
    });
  });
};

/**
 * 监听 MQTT 消息接收（全局监听，可多次调用添加多个回调）
 * @param callback 消息回调函数 (topic: 主题, payload: 消息内容字符串)
 */
const onMessage = (callback: (topic: string, payload: string) => void) => {
  if (!mqttClient) {
    console.warn("⚠️ 全局 MQTT 客户端未初始化，无法监听消息");
    return;
  }

  mqttClient.on("message", (topic, payload) => {
    callback(topic, payload.toString());
  });
};

/**
 * 主动关闭全局 MQTT 连接（一般无需调用，项目退出时自动释放）
 */
const close = () => {
  if (mqttClient) {
    mqttClient.end(true); // true: 强制关闭，不等待未发送消息
    mqttClient = null;
    isConnected = false;
    console.log("🔌 全局 MQTT 连接已主动关闭");
  }
};

// ===================== 5. 暴露全局 API（全项目导入使用）=====================
export const globalMqtt = {
  initMqtt,
  // 客户端实例（可选暴露，高级用法可直接操作）
  client: mqttClient,
  // 连接状态（全局可判断）
  isConnected: () => isConnected,
  // 全局错误信息
  getError: () => globalError,
  // 核心方法
  publish,
  subscribe,
  unsubscribe,
  onMessage,
  close,
};
// 🌟 导出默认值，简化导入（可选）
